import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { view } from "./AiChatView";
import useKeyPress from "../../utils/useKeyPress";
import { logAnalyticEvent } from "../../utils";
import {
  useSendChat,
  useGetPreviousChats,
  useGetPreviousChat,
  useRenameChat,
  useArchiveChat,
  useDeleteChat,
} from "../../store/queries/ai";

// const defaultParams = {
//   chatsize: 6,
//   score: 0.45,
//   maxresults: 6,
// };

export const AiChat = () => {
  const history = useHistory();
  const { id } = useParams();
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const enterPress = useKeyPress("Enter");

  const auth = useSelector((state) => state.auth.data);
  const initialExamples = useSelector((state) => state.ai.chat);
  const userPlans = useSelector((state) => state.plan.userPlans);
  const monitors = useSelector((state) => state.monitor.auth?.data || []);

  const [chatId, setChatId] = useState(id);
  const [data, setData] = useState([]);
  const [question, setQuestion] = useState("");
  const [waitForQuestion, setWaitForQuestion] = useState("");
  const [showAll, setShowAll] = useState(false);
  // const [regenerate, setRegenerate] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [formattedChat, setFormattedChat] = useState(null);

  const [examples, setExamples] = useState(initialExamples);
  // const [params, setParams] = useState(defaultParams);
  const [isExample, setIsExample] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    mutateAsync: chat,
    isLoading,
    isSuccess,
  } = useSendChat({
    question: question,
    chatId,
  });
  // all chats (left menu)
  const {
    data: chats,
    isFetching: chatsLoading,
    refetch: refetchChats,
  } = useGetPreviousChats(showAll ? undefined : 6);
  // current chat
  const {
    data: previousChat,
    isSuccess: prevSuccess,
    isFetching: chatLoading,
    refetch: refetchPreviousChat,
  } = useGetPreviousChat({
    chatId: chatId,
  });
  // actions
  const { mutateAsync: renameChat } = useRenameChat({
    chatId: selectedChat,
    label: newName,
  });
  const { mutateAsync: archiveChat } = useArchiveChat({
    chatId: selectedChat,
    status: 0,
  });
  const { mutateAsync: unArchiveChat } = useArchiveChat({
    chatId: selectedChat,
    status: 1,
  });
  const { mutateAsync: deleteChat } = useDeleteChat({
    chatId: selectedChat,
  });

  const sendChat = async () => {
    const resp = await chat();
    if (resp.data?.chat_id) {
      setWaitForQuestion("");
      setQuestion("");
      if (!chatId || resp.data?.chat_id?.toString() !== chatId) {
        const newChatId = resp.data.chat_id.toString();
        setChatId(newChatId);
        window.history.pushState(
          "AI chat",
          "AI chat",
          `/aiChat/${resp.data.chat_id}`
        );
        setFormattedChat([
          {
            id: 0,
            question: resp.data.query,
            response: resp.data.response,
          },
        ]);
      } else {
        refetchPreviousChat();
      }
    }

    inputRef.current?.focus();
    setIsExample(false);
  };

  useEffect(() => {
    // if previous page is 'aiChat', do not push amplitude event
    if (document.referrer && document.referrer?.indexOf("aiChat") < 0) {
      logAnalyticEvent(
        "AI Chat",
        {},
        auth?.id || null,
        auth?.displayName || null,
        auth?.email || null,
        auth?.createdAt || null,
        auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
          ?.preferenceStringValue || "MEDICAL",
        userPlans?.data?.[0]?.plan?.name || null
      );
    }
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo(0, 0);
    }
  }, [chatId]);

  useEffect(() => {
    if (showAll) {
      refetchChats();
    }
  }, [showAll]);

  useEffect(() => {
    const tempMonitors = monitors
      .filter((m) => m.visible && m.monitorComponent.object1Type === "DISEASE")
      .map((m) => m.monitorComponent.object1Name);

    if (tempMonitors.length > 0) {
      const firstRandomMonitor =
        tempMonitors[Math.floor(Math.random() * tempMonitors.length)];

      let secondRandomMonitor = firstRandomMonitor;
      if (tempMonitors.length > 1) {
        const remainingMonitors = tempMonitors.filter(
          (m) => m !== firstRandomMonitor
        );
        secondRandomMonitor =
          remainingMonitors[
            Math.floor(Math.random() * remainingMonitors.length)
          ];
      }

      const tempExamples = [...initialExamples];
      tempExamples[1].text = tempExamples[1].text.replace(
        "Acute Lymphocytic Leukemia",
        firstRandomMonitor
      );
      tempExamples[2].text = tempExamples[2].text.replace(
        "Hepatocellular Carcinoma",
        secondRandomMonitor
      );

      setExamples(tempExamples);
    }
  }, [monitors]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [waitForQuestion]);

  useEffect(() => {
    if (enterPress && question) {
      handleSubmitNewChat();
    }
  }, [enterPress]);

  const handleSubmitNewChat = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (question) {
      sendChat();

      logAnalyticEvent(
        "AI Chat - User Prompt",
        {
          prompt: question,
          ...(chatId && { chatId }),
        },
        auth?.id || null,
        auth?.displayName || null,
        auth?.email || null,
        auth?.createdAt || null,
        auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
          ?.preferenceStringValue || "MEDICAL",
        userPlans?.data?.[0]?.plan?.name || null
      );
    }

    if (!waitForQuestion) {
      setWaitForQuestion(question);
      setQuestion("");
    }
  };

  const handleRegenerate = () => {
    setQuestion("Give me more details");
    setIsExample(true);
    setTimeout(() => {
      setWaitForQuestion("Give me more details");
      sendChat();
    }, 200);

    logAnalyticEvent(
      "AI Chat - Regenerate",
      {},
      auth?.id || null,
      auth?.displayName || null,
      auth?.email || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue || "MEDICAL",
      userPlans?.data?.[0]?.plan?.name || null
    );
  };

  const handleExampleClick = (id) => {
    setIsExample(true);
    const selectedExample = initialExamples.find(
      (item) => item.id === id
    )?.text;
    if (selectedExample) {
      setWaitForQuestion(selectedExample);
      setQuestion(selectedExample);
      setChatId(null);
      setTimeout(() => {
        sendChat();
      }, 200);
    }

    logAnalyticEvent(
      "AI Chat - Example",
      {
        prompt: selectedExample,
      },
      auth?.id || null,
      auth?.displayName || null,
      auth?.email || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue || "MEDICAL",
      userPlans?.data?.[0]?.plan?.name || null
    );
  };

  const handleSessionClick = (chatId) => {
    window.location.href = `/aiChat/${chatId}`;

    logAnalyticEvent(
      "AI Chat - Previous Chat",
      { chatId },
      auth?.id || null,
      auth?.displayName || null,
      auth?.email || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue || "MEDICAL",
      userPlans?.data?.[0]?.plan?.name || null
    );
  };

  // event handler for 'Copy To Clipboard' link click
  const handleCopyToClipboard = () => {
    setShowToast(true);

    logAnalyticEvent(
      "AI Chat - Copy To Clipboard",
      {},
      auth?.id || null,
      auth?.displayName || null,
      auth?.email || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue || "MEDICAL",
      userPlans?.data?.[0]?.plan?.name || null
    );
  };

  // event handler for 'New Chat' button click
  const handleNewChatClick = () => {
    logAnalyticEvent(
      "AI Chat - New Chat",
      {},
      auth?.id || null,
      auth?.displayName || null,
      auth?.email || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue || "MEDICAL",
      userPlans?.data?.[0]?.plan?.name || null
    );

    window.location.href = "/aiChat";
  };

  useEffect(() => {
    if (scrollRef && chat.success) {
      setTimeout(() => {
        scrollRef.current.scrollIntoView({
          alignToTop: true,
          behavior: "smooth",
        });
      }, 200);
    }
  }, [chat.success]);

  const handleFormatChat = (chatHistory) => {
    const tempFormattedChat = [];
    for (let i = 0; i < chatHistory.length; i++) {
      const userMessage = chatHistory[i].replace(/\([^)]*\)/g, "");

      if (userMessage.includes("User message:")) {
        // Check if next message exists and is an AI message
        const nextMessage = chatHistory[i + 1];
        if (nextMessage && nextMessage.includes("AI message:")) {
          tempFormattedChat.push({
            id: i,
            question: userMessage.replace("User message:", "").trim(),
            response: nextMessage.replace("AI message:", "").trim(),
          });
          i++; // Skip the AI message in next iteration
        }
      }
    }
    setFormattedChat(tempFormattedChat);
  };

  useEffect(() => {
    if (previousChat?.data?.[0]?.chatHistory) {
      handleFormatChat(previousChat.data[0].chatHistory);
    }
  }, [previousChat]);

  useEffect(() => {
    if (prevSuccess && previousChat?.data?.length === 0) {
      history.push("/aiChat");
    }
  }, [prevSuccess]);

  const handleRenameChat = (item) => {
    setSelectedChat(item.chatId);
    setNewName(item.categorizationResponse.chatLabel);
    setShowRenameModal(true);
  };

  const handleSubmitRename = async () => {
    await renameChat();
    setShowRenameModal(false);
  };

  const handleArchiveChat = async (item) => {
    setSelectedChat(item.chatId);
    // Wait for state update to complete
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (item.status === 0) {
      await unArchiveChat();
    } else {
      await archiveChat();
    }
  };

  const handleDeleteChat = async () => {
    // Wait for state update to complete
    await new Promise((resolve) => setTimeout(resolve, 0));
    await deleteChat();
    setShowDeleteModal(false);
  };

  console.log("chats?.data", chats?.data);
  return view({
    isAdmin: auth?.mwRole === "ADMIN",
    data: formattedChat || [],
    dataSuccess: prevSuccess,
    chatLoading,
    chatsLoading,
    username: auth.userName,
    examples,
    previousChats: chats?.data || [],
    handleExampleClick,
    question,
    setQuestion,
    waitForQuestion,
    handleSubmitNewChat,
    handleSessionClick,
    scrollRef,
    showAll,
    setShowAll,
    // regenerate,
    handleRegenerate,
    isFetching: isLoading,
    isSuccess,

    showToast,
    setShowToast,

    handleCopyToClipboard,
    handleNewChatClick,

    bodyRef,
    isExample,
    inputRef,

    showRenameModal,
    setShowRenameModal,
    handleRenameChat,
    handleArchiveChat,
    handleDeleteChat,

    newName,
    setNewName,
    handleSubmitRename,
    showDeleteModal,
    setShowDeleteModal,
    setSelectedChat,
  });
};
